import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const InvoicedComponent = () => {
  const [file, setFile] = useState(null);
  const [invoicedData, setInvoicedData] = useState([]);
  const [auctionDate, setAuctionDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDatabaseConnected, setIsDatabaseConnected] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const API_URL = process.env.NODE_ENV === 'production' 
  ? ''
  : 'http://localhost:8000';
  // Check database connection on component mount
  useEffect(() => {
    const checkDbConnection = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/check-db-connection-invoiced`);
        setIsDatabaseConnected(response.data.connected);
        if (!response.data.connected) {
          setError('Database connection failed. Please check server configuration.');
        }
      } catch (err) {
        setIsDatabaseConnected(false);
        setError('Database connection failed. Please check server configuration.');
      }
    };
    checkDbConnection();
  }, []);

  // Handle file upload
  const handleFileUpload = (e) => {
    setError('');
    setSuccessMessage('');
    
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    // Check if it's an Excel file
    if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
      setError('Please upload an Excel file (.xlsx or .xls)');
      return;
    }
    
    setFile(selectedFile);
    
    // Extract date from filename (if possible)
    // Example formats: 
    // "PHONEBOT VF2501-1709 invoiced 24012025.xlsx"
    // "VF2410-1629 PhoneBot 23102024 invoiced.xlsx"
    try {
      // Look for an 8-digit number anywhere in the filename that's likely a date
      // It could be in various positions in the filename
      const dateMatch = selectedFile.name.match(/(\d{8})/);
      if (dateMatch && dateMatch[1]) {
        const dateStr = dateMatch[1]; // e.g. "23102024"
        // Format: DDMMYYYY to YYYY-MM-DD
        const day = dateStr.substring(0, 2);
        const month = dateStr.substring(2, 4);
        const year = dateStr.substring(4, 8);
        const formattedDate = `${year}-${month}-${day}`;
        setAuctionDate(formattedDate);
      } else {
        // If no 8-digit date, try to find a date in format DD-MM-YYYY or similar
        const dashDateMatch = selectedFile.name.match(/(\d{2})[-_.](\d{2})[-_.](\d{4})/);
        if (dashDateMatch && dashDateMatch.length >= 4) {
          const day = dashDateMatch[1];
          const month = dashDateMatch[2];
          const year = dashDateMatch[3];
          const formattedDate = `${year}-${month}-${day}`;
          setAuctionDate(formattedDate);
        } else {
          // If still no date in filename, set to today's date
          const today = new Date();
          const formattedDate = today.toISOString().split('T')[0];
          setAuctionDate(formattedDate);
        }
      }
    } catch (err) {
      console.error("Error extracting date from filename:", err);
      // Default to today if extraction fails
      const today = new Date();
      const formattedDate = today.toISOString().split('T')[0];
      setAuctionDate(formattedDate);
    }
  };

  // Process the Excel file
  const processFile = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // Assume data is in the first sheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          // Get the worksheet data with headers
          const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
          
          if (rawData.length === 0) {
            setIsLoading(false);
            setError('The Excel file is empty');
            return;
          }
          
          // Check if the first row has headers or if it's just a title row
          const firstRow = rawData[0];
          const secondRow = rawData.length > 1 ? rawData[1] : [];
          
          let startRow = 0; // Default to first row
          
          // Check if first row contains expected column names
          const hasDescription = firstRow.some(cell => 
            cell && (String(cell).toLowerCase().includes('description') || String(cell).toLowerCase().includes('product'))
          );
          const hasQty = firstRow.some(cell => 
            cell && String(cell).toLowerCase().includes('qty')
          );
          const hasPrice = firstRow.some(cell => 
            cell && (String(cell).toLowerCase().includes('price/1') || String(cell).toLowerCase().includes('price'))
          );
          
          // If first row doesn't have expected headers but second row does, use second row as header
          if (!hasDescription || !hasQty || !hasPrice) {
            const secondRowHasDescription = secondRow.some(cell => 
              cell && (String(cell).toLowerCase().includes('description') || String(cell).toLowerCase().includes('product'))
            );
            const secondRowHasQty = secondRow.some(cell => 
              cell && String(cell).toLowerCase().includes('qty')
            );
            const secondRowHasPrice = secondRow.some(cell => 
              cell && (String(cell).toLowerCase().includes('price/1') || String(cell).toLowerCase().includes('price'))
            );
            
            if (secondRowHasDescription && secondRowHasQty && secondRowHasPrice) {
              // Second row has the headers, so start from there
              startRow = 1;
            }
          }
          
          // Parse the data using the determined header row
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { range: startRow });
          
          if (jsonData.length === 0) {
            setIsLoading(false);
            setError('No data found in the Excel file after header row');
            return;
          }
          
          // Format the data for display and database
          const formattedData = jsonData.map(row => {
            // Find the column keys (they might have slight variations in naming)
            const descriptionKey = Object.keys(row).find(key => 
              key.toLowerCase().includes('description') || key.toLowerCase().includes('product')
            );
            const qtyKey = Object.keys(row).find(key => 
              key.toLowerCase().includes('qty')
            );
            const priceKey = Object.keys(row).find(key => 
              key.toLowerCase().includes('price/1') || key.toLowerCase().includes('price')
            );
            const totalPriceKey = Object.keys(row).find(key => 
              key.toLowerCase().includes('total price') || 
              key.toLowerCase().includes('total') || 
              key.toLowerCase().includes('amount')
            );
            
            // Check if all required keys are found
            if (!descriptionKey || !qtyKey || !priceKey || !totalPriceKey) {
              console.warn('Missing column for row:', row);
            }
            
            return {
              product_description: descriptionKey ? row[descriptionKey] : '',
              qty: qtyKey ? parseInt(row[qtyKey]) || 0 : 0,
              price: priceKey ? parseFloat(row[priceKey]) || 0 : 0,
              total_price: totalPriceKey ? parseFloat(row[totalPriceKey]) || 0 : 0,
              auction_date: auctionDate,
              source_file: file.name
            };
          });
          
          // Filter out any rows with empty product_description
          const validData = formattedData.filter(item => 
            item.product_description && 
            typeof item.product_description === 'string' && 
            item.product_description.trim() !== ''
          );
          
          if (validData.length === 0) {
            setIsLoading(false);
            setError('No valid data rows found in the Excel file');
            return;
          }
          
          setInvoicedData(validData);
          setIsLoading(false);
          
          // Show summary
          setSuccessMessage(`Successfully processed ${validData.length} invoiced items from ${file.name}`);
          
        } catch (error) {
          console.error('Error processing Excel file:', error);
          setIsLoading(false);
          setError('Error processing Excel file: ' + error.message);
        }
      };
      
      reader.onerror = () => {
        setIsLoading(false);
        setError('Error reading the file');
      };
      
      reader.readAsArrayBuffer(file);
      
    } catch (error) {
      console.error('Error reading file:', error);
      setIsLoading(false);
      setError('Error reading file: ' + error.message);
    }
  };

  // Save to database
  const saveToDatabase = async () => {
    if (!isDatabaseConnected) {
      setError('Cannot save: No database connection');
      return;
    }
    
    if (invoicedData.length === 0) {
      setError('No data to save. Please process a file first.');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API_URL}/api/save-invoiced-data`, {
        records: invoicedData
      });
      
      if (response.data.success) {
        setSuccessMessage(`Saved ${response.data.records_saved} invoiced records to database. Updated ${response.data.records_updated} auction data records.`);
        
        // Clear the data after successful save
        setFile(null);
        setInvoicedData([]);
        // Reset file input
        const fileInput = document.getElementById('file-upload');
        if (fileInput) fileInput.value = '';
      } else {
        setError('Failed to save data: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error saving to database:', error);
      setError('Error saving to database: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <ToastContainer position="top-right" autoClose={5000} />
      
      <h1 className="text-3xl font-bold text-gray-800 mb-6 border-b pb-2">Invoiced Auction Items</h1>
      
      {/* Connection status */}
      <div className="mb-6 flex items-center">
        <span className="mr-2 text-gray-700">Database Connection:</span>
        <span className={`font-semibold ${isDatabaseConnected ? 'text-emerald-600' : 'text-red-600'} flex items-center`}>
          {isDatabaseConnected ? (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              Connected
            </>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              Disconnected
            </>
          )}
        </span>
      </div>
      
      {/* File upload section */}
      <div className="mb-8 p-6 bg-white rounded-lg shadow-sm border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Upload Invoiced File</h2>
        
        <div className="mb-5">
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Invoiced Excel File:</label>
          <div className="flex items-center">
            <label className="flex-1 cursor-pointer bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none">
              <span>{file ? file.name : "Choose file..."}</span>
              <input
                id="file-upload"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                className="sr-only"
              />
            </label>
          </div>
        </div>
        
        {file && (
          <div className="mb-5 p-4 bg-blue-50 rounded-md border border-blue-200">
            <p className="text-sm font-medium text-blue-800">Selected File: <span className="font-semibold">{file.name}</span></p>
            <p className="text-sm mt-1 text-blue-700">Extracted Auction Date: <span className="font-semibold">{auctionDate}</span></p>
          </div>
        )}
        
        <div className="flex space-x-4">
          <button
            onClick={processFile}
            disabled={!file || isLoading}
            className={`px-4 py-2 rounded-md text-white font-medium transition-colors ${
              !file || isLoading 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-blue-600 hover:bg-blue-700 shadow-sm'
            }`}
          >
            {isLoading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </span>
            ) : 'Process File'}
          </button>
          
          <button
            onClick={saveToDatabase}
            disabled={invoicedData.length === 0 || !isDatabaseConnected || isLoading}
            className={`px-4 py-2 rounded-md text-white font-medium transition-colors ${
              invoicedData.length === 0 || !isDatabaseConnected || isLoading
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-emerald-600 hover:bg-emerald-700 shadow-sm'
            }`}
          >
            {isLoading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </span>
            ) : 'Save to Database'}
          </button>
        </div>
      </div>
      
      {/* Error/Success messages */}
      {error && (
        <div className="mb-6 p-4 rounded-md bg-red-50 border-l-4 border-red-500">
          <div className="flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-red-700 font-medium">{error}</span>
          </div>
        </div>
      )}
      
      {successMessage && (
        <div className="mb-6 p-4 rounded-md bg-green-50 border-l-4 border-green-500">
          <div className="flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-green-700 font-medium">{successMessage}</span>
          </div>
        </div>
      )}
      
      {/* Results Table */}
      {invoicedData.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Processed Invoiced Data</h2>
            <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-1 rounded-full">{invoicedData.length} items</span>
          </div>
          
          <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gray-50">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Price/Item</th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Price</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invoicedData.map((item, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-normal text-sm text-gray-900">{item.product_description}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900">{item.qty}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 font-medium">£{item.price.toFixed(2)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 font-medium">£{item.total_price.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50">
                    <td colSpan="3" className="px-6 py-3 text-right text-sm font-medium text-gray-500">Total:</td>
                    <td className="px-6 py-3 text-right text-sm font-medium text-gray-900">
                      £{invoicedData.reduce((total, item) => total + item.total_price, 0).toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicedComponent;